import { useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, differenceInDays, addDays } from 'date-fns';
import { Task, Category } from '../../types';

interface GanttChartProps {
  tasks: Task[];
  categories: Category[];
  onTaskClick: (task: Task) => void;
}

export const GanttChart = ({ tasks, categories, onTaskClick }: GanttChartProps) => {
  const { dateRange, dayWidth } = useMemo(() => {
    if (tasks.length === 0) {
      const today = new Date();
      return {
        dateRange: eachDayOfInterval({
          start: startOfMonth(today),
          end: endOfMonth(today),
        }),
        dayWidth: 40,
      };
    }

    const allDates = tasks.flatMap(task => [task.startDate, task.endDate]);
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    
    const start = startOfMonth(minDate);
    const end = endOfMonth(addDays(maxDate, 30));
    
    return {
      dateRange: eachDayOfInterval({ start, end }),
      dayWidth: 40,
    };
  }, [tasks]);

  const getTaskPosition = (task: Task) => {
    const startIndex = differenceInDays(task.startDate, dateRange[0]);
    const duration = differenceInDays(task.endDate, task.startDate) + 1;
    
    return {
      left: startIndex * dayWidth,
      width: duration * dayWidth,
    };
  };

  const getCategoryColor = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)?.color || '#6B7280';
  };

  const mainTasks = tasks.filter(task => !task.parentId);
  const subTasks = tasks.filter(task => task.parentId);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Project Timeline</h2>
      </div>
      
      <div className="overflow-x-auto">
        <div className="min-w-max">
          {/* Header */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <div className="w-80 p-3 font-medium text-gray-700 border-r border-gray-200">
              Task Name
            </div>
            <div className="flex">
              {dateRange.map((date, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center border-r border-gray-200 bg-gray-50"
                  style={{ width: dayWidth }}
                >
                  <div className="text-xs font-medium text-gray-600">
                    {format(date, 'MMM')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {format(date, 'd')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tasks */}
          <div className="relative">
            {mainTasks.map((task, taskIndex) => {
              const taskSubTasks = subTasks.filter(sub => sub.parentId === task.id);
              const position = getTaskPosition(task);
              const categoryColor = getCategoryColor(task.categoryId);

              return (
                <div key={task.id}>
                  {/* Main Task */}
                  <div className="flex border-b border-gray-100 hover:bg-gray-50">
                    <div className="w-80 p-3 border-r border-gray-200">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: categoryColor }}
                        />
                        <span className="font-medium text-gray-900">{task.name}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {format(task.startDate, 'MMM d')} - {format(task.endDate, 'MMM d')}
                      </div>
                    </div>
                    <div className="relative flex-1 p-2">
                      <div
                        className="absolute top-2 h-6 rounded cursor-pointer transition-all duration-200 hover:opacity-80"
                        style={{
                          left: position.left,
                          width: position.width,
                          backgroundColor: categoryColor,
                        }}
                        onClick={() => onTaskClick(task)}
                      >
                        <div 
                          className="h-full bg-white bg-opacity-30 rounded"
                          style={{ width: `${task.progress}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs font-medium text-white truncate">
                            {task.progress}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sub Tasks */}
                  {taskSubTasks.map((subTask) => {
                    const subPosition = getTaskPosition(subTask);
                    const subCategoryColor = getCategoryColor(subTask.categoryId);

                    return (
                      <div key={subTask.id} className="flex border-b border-gray-100 hover:bg-gray-50">
                        <div className="w-80 p-3 border-r border-gray-200">
                          <div className="flex items-center gap-2 ml-6">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: subCategoryColor }}
                            />
                            <span className="text-sm text-gray-700">{subTask.name}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 ml-6">
                            {format(subTask.startDate, 'MMM d')} - {format(subTask.endDate, 'MMM d')}
                          </div>
                        </div>
                        <div className="relative flex-1 p-2">
                          <div
                            className="absolute top-2 h-4 rounded cursor-pointer transition-all duration-200 hover:opacity-80"
                            style={{
                              left: subPosition.left,
                              width: subPosition.width,
                              backgroundColor: subCategoryColor,
                            }}
                            onClick={() => onTaskClick(subTask)}
                          >
                            <div 
                              className="h-full bg-white bg-opacity-30 rounded"
                              style={{ width: `${subTask.progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};